// /admin/health — read-only dashboard for the worker's heartbeat doc.
// Owner-gated: members see a 403-ish "owner-only" panel. Real
// authorization happens via firestore.rules (only members of any team can
// read worker_metrics); the role check below is UI-level for clarity.

import { useEffect, useState } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { STALE_THRESHOLD_MS, isHeartbeatStale, useWorkerHeartbeat } from '../../lib/admin-health.js'
import { useMyTeams } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'

function fmtTime(t) {
  if (!t) return '—'
  const ms = typeof t === 'string' ? new Date(t).getTime() : (t.toMillis?.() ?? Number(t))
  if (!ms) return '—'
  return new Date(ms).toLocaleString()
}

function fmtUptime(secs) {
  if (!secs) return '—'
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = Math.floor(secs % 60)
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function MetricCard({ label, value, unit }) {
  return (
    <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] px-5 py-4">
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
        {label}
      </div>
      <div className="font-mono text-2xl text-[var(--color-basalt)]">
        {value}
        {unit && <span className="ml-1 text-xs text-[var(--color-schist)]">{unit}</span>}
      </div>
    </div>
  )
}

export function AdminHealth() {
  const [user] = useAuthState(auth)
  const { data: teams } = useMyTeams(user?.uid)
  const { data: heartbeat, loading, error } = useWorkerHeartbeat()
  const [, setTick] = useState(0)

  // Re-render every 10s so "X seconds ago" stays accurate (the underlying
  // snapshot listener already pushes new heartbeats automatically).
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000)
    return () => clearInterval(id)
  }, [])

  const isOwner = teams.some((t) => t.role === 'owner')
  if (!isOwner) {
    return (
      <>
        <PageHeader title="Admin health" subhead="Owner-only" />
        <div className="mx-auto max-w-2xl px-6 py-12">
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8 text-center">
            <p className="text-sm text-[var(--color-slate)]">
              The worker health page is gated to team owners. Contact your team owner if you need
              access.
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/teams"
                className="text-[var(--color-terracotta)] underline-offset-4 hover:underline"
              >
                Back to teams →
              </Link>
            </p>
          </div>
        </div>
      </>
    )
  }

  const stale = isHeartbeatStale(heartbeat)
  const statusCls = !heartbeat
    ? 'border-[var(--color-failed)] text-[var(--color-failed)]'
    : stale
      ? 'border-[var(--color-failed)] text-[var(--color-failed)]'
      : 'border-[var(--color-passed)] text-[var(--color-passed)]'

  const concurrency = heartbeat?.configuredConcurrency ?? {}

  return (
    <>
      <PageHeader
        title="Admin health"
        subhead="Worker heartbeat + queue health"
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={() => setTick((n) => n + 1)}>
            Refresh
          </Button>
        }
      />

      <div className="mx-auto max-w-4xl px-6 py-8">
        {error && (
          <p className="mb-4 text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}
        {loading && !heartbeat && (
          <p className="text-sm text-[var(--color-schist)]">Loading heartbeat…</p>
        )}

        <div
          className={`mb-6 flex items-center justify-between gap-3 rounded-md border px-5 py-3 ${statusCls}`}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm uppercase tracking-wider">
              {!heartbeat ? 'no heartbeat' : stale ? 'stale' : 'healthy'}
            </span>
            <span className="text-xs">last beat: {fmtTime(heartbeat?.lastBeat)}</span>
          </div>
          <span className="text-xs">stale threshold: {STALE_THRESHOLD_MS / 60_000} min</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard label="Process ID" value={heartbeat?.pid ?? '—'} />
          <MetricCard label="Memory (RSS)" value={heartbeat?.memMb ?? '—'} unit="MB" />
          <MetricCard label="Heap used" value={heartbeat?.heapUsedMb ?? '—'} unit="MB" />
          <MetricCard label="Uptime" value={fmtUptime(heartbeat?.uptimeSeconds)} />
          <MetricCard label="voice-to-graph cap" value={concurrency['voice-to-graph'] ?? '—'} />
          <MetricCard label="run-test cap" value={concurrency['run-test'] ?? '—'} />
          <MetricCard label="scan-app cap" value={concurrency['scan-app'] ?? '—'} />
        </div>

        <p className="mt-8 text-xs text-[var(--color-schist)]">
          Logs ship to Cloud Logging from the worker's Docker stdout. Every log line carries the
          job's traceId for cross-system correlation. Push-style alerting (Slack / equivalent) is
          deferred to a later slice.
        </p>
      </div>
    </>
  )
}
